import type { NpiSuggestion } from '@seen/react'
import { parseResponse } from 'hono/client'
import { useMemo } from 'react'
import { usePortalAiClient } from '#src/api/ai.ts'

// A registry organization match surfaced to the practitioner-create form.
export interface NpiOrgSuggestion {
  npi: string
  name?: string
  phone?: string
  fax?: string
  address?: {
    line1?: string
    line2?: string
    city?: string
    state?: string
    postalCode?: string
  }
}

// A provider's Medicare group affiliation (from the CMS NDF).
export interface NpiAffiliationSuggestion {
  facilityName?: string
  orgPacId?: string
  phone?: string
  address?: {
    line1?: string
    line2?: string
    city?: string
    state?: string
    postalCode?: string
  }
}

// Imperative NPI-registry callers bound to the authed portal client. Passed as
// props into the @seen/react PractitionerCreateForm, which stays free of app
// and server types.
export interface PortalNpiApi {
  searchIndividuals: (query: string) => Promise<NpiSuggestion[]>
  searchOrganizations: (params: {
    q?: string
    number?: string
    city?: string
    state?: string
    postalCode?: string
  }) => Promise<NpiOrgSuggestion[]>
  getAffiliations: (npi: string) => Promise<NpiAffiliationSuggestion[]>
}

export function createPortalNpiApi(
  client: ReturnType<typeof usePortalAiClient>
): PortalNpiApi {
  return {
    searchIndividuals: async (query) => {
      // The server's default limit (10) can truncate before a common-name
      // match appears; request the server's own max instead.
      const { results } = await parseResponse(
        client.api.npiLookup.$get({ query: { q: query, limit: '50' } })
      )
      return results.filter((r) => r.kind === 'individual')
    },
    searchOrganizations: async (params) => {
      // A practice-location lookup returns every org at a ZIP+4; request the
      // server max so the target isn't truncated before the client filters it.
      const { results } = await parseResponse(
        client.api.npiLookup.$get({
          query: { ...params, type: 'organization', limit: '50' },
        })
      )
      return results
        .filter((r) => r.kind === 'organization')
        .map((r) => ({
          npi: r.npi,
          name: r.name,
          dba: r.dba,
          phone: r.phone,
          fax: r.fax,
          address: r.address,
        }))
    },
    getAffiliations: async (npi) => {
      const { affiliations } = await parseResponse(
        client.api.npiAffiliations.$get({ query: { number: npi } })
      )
      return affiliations
    },
  }
}

export function usePortalNpiApi(): PortalNpiApi {
  const client = usePortalAiClient()

  return useMemo(() => createPortalNpiApi(client), [client])
}
